import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// "Please confirm your home address" (HR, 01/10/2026). The address is fetched
// here, for the signed-in person only -- it is never part of the message, so
// it cannot leak through Dispatch, read reports, email or reminders.
// The same component lives in the dashboard and the FSM (copied, not shared);
// change both together.

const FIELDS = [
  ['line1', 'Address line 1', true],
  ['line2', 'Address line 2', false],
  ['line3', 'Address line 3', false],
  ['town', 'Town or city', true],
  ['county', 'County', false],
  ['postcode', 'Postcode', true],
  ['country', 'Country', true],
];
const COUNTRIES = ['United Kingdom', 'South Africa'];
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '');

function AddressLines({ a }) {
  return (
    <address className="not-italic leading-relaxed text-gray-900">
      {[a.line1, a.line2, a.line3, a.town, a.county].filter(Boolean).map((l) => <div key={l}>{l}</div>)}
      {a.postcode ? <div className="font-semibold tracking-wide">{a.postcode}</div> : null}
      {a.country ? <div>{a.country}</div> : null}
    </address>
  );
}

export default function AddressConfirmPanel({ requestId, big = false }) {
  const qc = useQueryClient();
  const key = ['addressConfirm', String(requestId)];
  const { data: r, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get(`/api/comms/address/${requestId}`)).data,
    staleTime: 0,
  });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const done = (data) => {
    qc.setQueryData(key, data);
    qc.invalidateQueries({ queryKey: ['commInbox'] });
    setEditing(false); setErr(''); setBusy(false);
  };
  const fail = (e) => { setErr(e?.response?.data?.error || 'That did not save. Please try again.'); setBusy(false); };
  const confirm = () => { setBusy(true); setErr(''); api.post(`/api/comms/address/${requestId}/confirm`).then((x) => done(x.data)).catch(fail); };
  const save = () => { setBusy(true); setErr(''); api.post(`/api/comms/address/${requestId}/update`, form).then((x) => done(x.data)).catch(fail); };
  const startEdit = () => {
    setForm({ ...(r?.on_record || {}), ...(r?.country_locked ? { country: r.country_locked } : {}), note: '' });
    setEditing(true); setErr('');
  };

  const btn = big ? 'px-4 py-3 text-base' : 'px-4 py-2.5 text-sm';
  const input = `w-full rounded-md border border-gray-300 px-3 ${big ? 'py-2.5 text-base' : 'py-2 text-sm'} focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand`;

  if (isLoading) return <p className="mt-5 text-sm text-gray-400">Loading your address…</p>;
  if (isError || !r) return <p className="mt-5 text-sm text-red-600">Your address could not be loaded. Please try again later.</p>;

  const answered = r.status !== 'pending';

  return (
    <div className="mt-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      {!editing ? (
        <>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {r.has_address ? 'The home address we hold for you' : 'Your home address'}
          </p>
          {r.has_address ? (
            <div className="mt-2 rounded-lg bg-gray-50 px-4 py-3"><AddressLines a={r.on_record} /></div>
          ) : (
            <p className="mt-2 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
              We do not have a home address on record for you. Please add it.
            </p>
          )}

          {answered ? (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {r.status === 'updated' ? 'Thanks, your new address has been sent to HR' : 'Thanks, you confirmed this address'}
              {r.responded_at ? ` on ${fmtDate(r.responded_at)}` : ''}. If it changes, you can update it here.
            </div>
          ) : null}

          {err ? <p className="mt-3 text-sm text-red-600">{err}</p> : null}

          <div className={`mt-4 grid gap-2 ${r.has_address && !answered ? 'sm:grid-cols-2' : ''}`}>
            {r.has_address && !answered ? (
              <button type="button" onClick={confirm} disabled={busy}
                className={`rounded-lg bg-brand font-semibold text-white hover:bg-brand-dark disabled:opacity-50 ${btn}`}>
                {busy ? 'Saving…' : 'Yes, this is correct'}
              </button>
            ) : null}
            <button type="button" onClick={startEdit} disabled={busy}
              className={`rounded-lg border font-semibold disabled:opacity-50 ${btn} ${r.has_address
                ? 'border-gray-300 bg-white text-gray-800 hover:border-brand hover:text-brand'
                : 'border-brand bg-brand text-white hover:bg-brand-dark'}`}>
              {!r.has_address ? 'Add my address' : answered ? 'Update my address' : 'No, update it'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your correct home address</p>
          <div className="mt-3 space-y-3">
            {FIELDS.map(([k, label, required]) => (k === 'country' && r.country_locked ? (
              // Set by HR from the person's employer (UK staff / Veldora); not theirs to change.
              <div key={k}>
                <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
                <div className={`${input} flex items-center justify-between bg-gray-100 text-gray-700`}>
                  {r.country_locked}
                  <span className="text-xs text-gray-400">Set by HR</span>
                </div>
              </div>
            ) : k === 'country' ? (
              <label key={k} className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">{label}<span className="text-red-500"> *</span></span>
                <input list="address-countries" value={form.country || ''} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  autoComplete="country-name" className={input} />
                <datalist id="address-countries">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist>
              </label>
            ) : (
              <label key={k} className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">
                  {label}{required ? <span className="text-red-500"> *</span> : <span className="text-gray-400"> (optional)</span>}
                </span>
                <input value={form[k] || ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  autoComplete={{ line1: 'address-line1', line2: 'address-line2', line3: 'address-line3', town: 'address-level2', county: 'address-level1', postcode: 'postal-code' }[k]}
                  className={`${input} ${k === 'postcode' ? 'uppercase' : ''}`} />
              </label>
            )))}
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Anything HR should know <span className="text-gray-400">(optional)</span></span>
              <textarea rows={2} value={form.note || ''} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="e.g. moved on 01/09/2026" className={input} />
            </label>
          </div>
          {err ? <p className="mt-3 text-sm text-red-600">{err}</p> : null}
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <button type="button" onClick={() => { setEditing(false); setErr(''); }} disabled={busy}
              className={`rounded-lg border border-gray-300 bg-white font-semibold text-gray-700 ${btn}`}>Cancel</button>
            <button type="button" onClick={save} disabled={busy}
              className={`rounded-lg bg-brand font-semibold text-white hover:bg-brand-dark disabled:opacity-50 ${btn}`}>
              {busy ? 'Saving…' : 'Send to HR'}
            </button>
          </div>
        </>
      )}
      <p className="mt-3 text-xs text-gray-400">Only you and HR can see your address.</p>
    </div>
  );
}
